import { useEffect, useMemo, useRef, useState } from "react";
import { mapShareImageUrl } from "../lib/map-share";
import { gamePrompt } from "../lib/in-game-dialog";
import { InGameDialogHost } from "./InGameDialogHost";

type PublicMapStatus = "loading" | "ready" | "unavailable";

export function PublicMapViewer({ shareId }: { shareId?: string }) {
  const [status, setStatus] = useState<PublicMapStatus>(shareId ? "loading" : "unavailable");
  const [copied, setCopied] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const frameRef = useRef<HTMLElement>(null);
  const previousShareId = useRef(shareId);
  const imageUrl = useMemo(() => shareId ? mapShareImageUrl(shareId) : undefined, [shareId]);
  const canFullscreen = typeof document.documentElement.requestFullscreen === "function";

  useEffect(() => {
    // State is initialized for the first ID. Reset only on a real ID change so
    // a fast cached image cannot fire onLoad and then be put back into loading.
    if (previousShareId.current === shareId) return;
    previousShareId.current = shareId;
    setStatus(shareId ? "loading" : "unavailable"); setAttempt(0); setCopied(false);
  }, [shareId]);

  const share = async () => {
    const url = window.location.href;
    try {
      if (typeof navigator.share === "function") {
        await navigator.share({ title: "Shared TBLTOP map", url });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1_800);
      } else await gamePrompt("Copy shared map link", url, "Select and copy this link", "Done");
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      setCopied(false);
    }
  };

  const fullscreen = async () => {
    const target = frameRef.current;
    if (!target || !canFullscreen) return;
    try { if (document.fullscreenElement) await document.exitFullscreen(); else await target.requestFullscreen(); } catch { /* The browser can decline fullscreen without making the map unusable. */ }
  };

  const retry = () => { if (!imageUrl) return; setStatus("loading"); setAttempt((value) => value + 1); };

  return <main className="public-map-viewer"><InGameDialogHost />
    <header className="public-map-toolbar">
      <a className="public-map-brand" href="/" aria-label="TBLTOP home"><span>TT</span><strong>TBLTOP</strong></a>
      <span className="public-map-title">Shared map</span>
      {status === "ready" ? <nav aria-label="Map actions">
        <button type="button" onClick={() => void share()}>{copied ? "Link copied" : "Share"}</button>
        {imageUrl ? <a href={imageUrl} download="TBLTOP-shared-map.png">Download</a> : null}
        {canFullscreen ? <button type="button" onClick={() => void fullscreen()}>Full screen</button> : null}
      </nav> : null}
    </header>
    <section ref={frameRef} className={`public-map-frame is-${status}`} aria-live="polite">
      {status === "loading" ? <div className="public-map-state" role="status"><span className="public-map-loader" aria-hidden="true" />Loading map…</div> : null}
      {imageUrl && status !== "unavailable" ? <img key={`${imageUrl}:${attempt}`} src={imageUrl} alt="Shared TBLTOP map" onLoad={() => setStatus("ready")} onError={() => setStatus("unavailable")} /> : null}
      {status === "unavailable" ? <div className="public-map-state"><strong>Map unavailable</strong><span>The link expired, was revoked, or could not be loaded.</span>{imageUrl ? <button type="button" onClick={retry}>Retry</button> : null}</div> : null}
    </section>
  </main>;
}
